// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getProductIdsForCompare,
    getProductsForCompare,
    MAX_PRODUCTS_TO_COMPARE,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import loadProductIdsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductIdsToCompare";
import loadProductsToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/LoadProductsToCompare";
import removeAllProductsFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveAllProductsFromCompare";
import removeProductIdToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import setProductCompareReturnUrl from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/SetProductCompareReturnUrl";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { CarouselSlide } from "@insite/content-library/Components/Carousel";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Page, { PageProps } from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@keller/App/Store/Reducers";
import ProductCarouselProductCondensed from "@keller/Overrides/Components/ProductCarouselProductCondensed";
import ArrowDown from "@keller/XDL/Icons/ArrowDown";
import ArrowUp from "@keller/XDL/Icons/ArrowUp";
import Close from "@keller/XDL/Icons/Close";
import { color, getSystemColor, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const PRODUCT_COMPARE_CAROUSEL_ID = "product-compare-carousel-id";

interface OwnProps {
    extendedStyles?: ProductCompareFlyOutStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapStateToProps = (state: CustomAppState) => ({
    productIds: getProductIdsForCompare(state),
    products: getProductsForCompare(state),
    productComparePageLink: getPageLinkByPageType(state, "ProductComparePage"),
    currentLocation: getLocation(state),
});

const mapDispatchToProps = {
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
};

interface CompareHeaderStyles {
    titleText?: TypographyPresentationProps;
    container?: PageProps;
    toggle?: ClickableProps;
}

export interface ProductCompareFlyOutStyles {
    headerRemoveAllButton?: LinkPresentationProps;
    headerCompareButton?: ButtonPresentationProps;
    carouselDrawer?: DrawerPresentationProps;
    carouselDrawerContainer?: InjectableCss;
    removeProductFromCompareButton?: ClickablePresentationProps;
    // Xng-Changes
    compareHeaderStyles?: CompareHeaderStyles;
    emptyItem?: InjectableCss;
    emptyItemText?: TypographyPresentationProps;
    actionsWrapper?: InjectableCss;
    slidesWrapper?: InjectableCss;
}

export const productCompareFlyOutStyles: ProductCompareFlyOutStyles = {
    carouselDrawer: {
        position: "bottom",
        cssOverrides: {
            scrim: css`
                transition: 300ms ease-in-out;
                background-color: transparent;
            `,
            drawerTitle: css`
                display: none;
            `,
            drawerBody: css`
                background: none;
                box-shadow: none;
            `,
            drawerContent: css`
                background-color: transparent;
                overflow-y: hidden;
                ${xScreen("lte", "xs")} {
                    height: 100%;
                }
            `,
        },
        closeButtonProps: {
            css: css`
                display: none;
            `,
        },
        enableClickThrough: true,
    },
    carouselDrawerContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            background: ${color("common.ui4")};
            padding: 15px 0;
            ${xScreen("lte", "xs")} {
                height: 100%;
            }
        `,
    },
    removeProductFromCompareButton: {
        css: css`
            position: absolute;
            right: 4px;
            top: 4px;
            padding: 8px;
            border-radius: 50%;
            &:hover {
                background-color: ${color("common.ui12")};
                ${IconWrapper} {
                    color: ${color("danger.main", 0.6)};                    
                }
            }
        `,
    },
    headerRemoveAllButton: {
        color: "secondary",
        hoverStyle: {
            color: getSystemColor("danger"),
        },
        typographyProps: {
            size: 13,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
    },
    headerCompareButton: {
        typographyProps: {
            size: 13,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
            `,
        },
        css: css`
            margin-bottom: 15px;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    compareHeaderStyles: {
        container: {
            css: css`
                padding: 0 20px !important;
                height: 36px;
                background-color: transparent;
            `,
        },
        titleText: {
            size: 16,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                margin-left: 10px;
                color: ${color("text.main")};
            `,
        },
        toggle: {
            css: css`
                align-items: center;
                justify-content: center;
                padding: 10px 25px;
                display: inline-flex;
                background: ${color("common.ui4")};
                &:focus {
                    outline: none;
                }
            `,
        },
    },
    emptyItem: {
        css: css`
            width: 130px;
            height: 130px;
            padding: 5px;
            border-radius: 4px;
            background-color: ${color("common.white")};
            box-shadow: 0 0 9px 0 ${color("primary", 0.15)};
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    emptyItemText: {
        size: 15,
        weight: "normal",
        css: css`
            width: 130px;
            letter-spacing: 0;
            line-height: 17px;
            text-align: center;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 0 40px;
            ${xScreen("lte", "xs")} {
                padding: 10px 40px;
                width: 100%;
                height: 100%;
                display: flex;
                justify-content: center;
                overflow: hidden;
            }
        `,
    },

    slidesWrapper: {
        css: css`
            display: flex;
            overflow-x: auto;
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            padding: 10px 0 10px 40px;
            & > div {
                scroll-snap-align: start;
                margin-right: 45px;
                &:last-child {
                    margin-right: 5px;
                }
            }

            ${xScreen("lte", "xs")} {
                display: none;
            }
        `,
    },
};

const mapProductsToSlides = (
    products: ProductModel[],
    styles: ProductCompareFlyOutStyles,
    removeProductIdToCompare: (productId: string) => void,
) =>
    products.map(product => ({
        id: product.id,
        renderComponent: function ProductCarouselProductCondensedRenderer() {
            return (
                <ProductCarouselProductCondensed
                    carouselId={PRODUCT_COMPARE_CAROUSEL_ID}
                    product={product}
                    condensedActionsTemplate={(product: ProductModel) => {
                        return (
                            <Clickable
                                {...styles.removeProductFromCompareButton}
                                aria-label="Close"
                                onClick={() => removeProductIdToCompare(product.id)}
                            >
                                <Icon src={Close} size={10} color="secondary" />
                            </Clickable>
                        );
                    }}
                />
            );
        },
    }));

const ProductCompareFlyOut = ({
    extendedStyles,
    history,
    productIds,
    products,
    productComparePageLink,
    currentLocation,
    loadProductIdsToCompare,
    loadProductsToCompare,
    removeProductIdToCompare,
    removeAllProductsFromCompare,
    setProductCompareReturnUrl,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productCompareFlyOutStyles, extendedStyles));

    const [slides, setSlides] = useState<CarouselSlide[]>([]);
    // Xng-changes
    const [open, setOpen] = useState<boolean>(true);

    useEffect(() => {
        // We load in the ProductIds to Compare from storage
        loadProductIdsToCompare();
    }, []);

    useEffect(() => {
        // Load in products from the ProductCompareService Compare Storage
        if (productIds.length === 0) {
            return;
        }

        loadProductsToCompare({ id: PRODUCT_COMPARE_CAROUSEL_ID, productIds, includeAttributeTypes: true });
    }, [productIds]);

    useEffect(() => {
        if (products.length === 0) {
            return;
        }
        setSlides(mapProductsToSlides(products, styles, productId => removeProductIdToCompare({ productId })));
    }, [products, styles]);

    const handleRemoveAll = () => {
        removeAllProductsFromCompare();
    };
    const handleOpenCompare = () => {
        if (productComparePageLink) {
            setProductCompareReturnUrl({ returnUrl: currentLocation.pathname + currentLocation.search });
            history.push(productComparePageLink.url);
        }
    };

    if (products.length === 0) {
        return null;
    }

    const hasMoreItems = MAX_PRODUCTS_TO_COMPARE - slides.length;

    // XNG-Changes: update structure
    return (
        <Drawer
            draggable={false}
            isOpen={true}
            size={open ? 216 : 36}
            {...styles.carouselDrawer}
            data-test-selector="productCompareDrawer"
        >
            <ProductCompareHeader
                open={open}
                onToggle={() => setOpen(!open)}
                styles={styles}
                productCount={products.length}
            />
            <StyledWrapper {...styles.carouselDrawerContainer}>
                <StyledWrapper {...styles.slidesWrapper}>
                    {slides.map(slide => {
                        return <StyledWrapper key={slide.id}>{slide.renderComponent({})}</StyledWrapper>;
                    })}
                    {hasMoreItems ? (
                        <StyledWrapper {...styles.emptyItem}>
                            <Typography {...styles.emptyItemText}>
                                {translate(
                                    "You can compare up to {0} more {1}",
                                    hasMoreItems === 1 ? "One" : `${hasMoreItems}`,
                                    hasMoreItems === 1 ? "item" : "items",
                                )}
                            </Typography>
                        </StyledWrapper>
                    ) : null}
                </StyledWrapper>

                <StyledWrapper {...styles.actionsWrapper}>
                    <Button
                        {...styles.headerCompareButton}
                        onClick={handleOpenCompare}
                        data-test-selector="productCompareOpenComparePageButton"
                    >
                        {translate("Compare")}
                    </Button>
                    <Link
                        {...styles.headerRemoveAllButton}
                        onClick={handleRemoveAll}
                        data-test-selector="productCompareRemoveAllButton"
                    >
                        {translate("Clear All")}
                    </Link>
                </StyledWrapper>
            </StyledWrapper>
        </Drawer>
    );
};

interface ProductCompareHeaderProps {
    styles: ProductCompareFlyOutStyles;
    productCount: number;
    open: boolean;
    onToggle: () => void;
}

const ProductCompareHeader = ({ styles, productCount, open, onToggle }: ProductCompareHeaderProps) => {
    return (
        <Page {...styles.compareHeaderStyles?.container}>
            <Clickable {...styles.compareHeaderStyles?.toggle} onClick={onToggle}>
                <Icon src={open ? ArrowDown : ArrowUp} size={12} color="primary" />
                <Typography {...styles.compareHeaderStyles?.titleText}>
                    {translate(`Compare {0} of ${MAX_PRODUCTS_TO_COMPARE} Items`, productCount.toString())}
                </Typography>
            </Clickable>
        </Page>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ProductCompareFlyOut));
